import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { grainTypeMeta, ZONE_MOVE_HOURS } from "@/lib/growthLogic";
import { LayoutGrid, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const TRAYS_PER_SHELF = 3;
const SHELVES_PER_RACK = 4; // visual grouping only
const INLINE_LIMIT = 4; // ≤ this many zones → inline cards; ≥5 → tab buttons

// Two visual themes: soak (slate) and growth (emerald/teal alternating)
const KIND_THEME = {
  soak: {
    label: "Soak & Dark Phase",
    bg: "bg-slate-50",
    border: "border-slate-200",
    accent: "text-slate-700",
    tray: "bg-slate-700",
    rackBg: "bg-white/80 border-slate-200",
    headerChip: "bg-slate-700 text-white",
    btnTab: "data-[active=true]:bg-slate-700 data-[active=true]:text-white",
  },
  growth: {
    label: "Growth Zone",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    accent: "text-emerald-800",
    tray: "bg-emerald-600",
    rackBg: "bg-white/80 border-emerald-200",
    headerChip: "bg-emerald-600 text-white",
    btnTab: "data-[active=true]:bg-emerald-600 data-[active=true]:text-white",
  },
};

const GROWTH_ALT_THEME = {
  ...KIND_THEME.growth,
  bg: "bg-teal-50",
  border: "border-teal-200",
  accent: "text-teal-800",
  tray: "bg-teal-600",
  rackBg: "bg-white/80 border-teal-200",
  headerChip: "bg-teal-600 text-white",
  btnTab: "data-[active=true]:bg-teal-600 data-[active=true]:text-white",
};

/** Pick a theme per zone — alternate growth color so multiple growth zones don't blur together. */
function themeForZone(zone, growthIndex) {
  if (zone.kind === "soak") return KIND_THEME.soak;
  return growthIndex % 2 === 0 ? KIND_THEME.growth : GROWTH_ALT_THEME;
}

function zoneCapacity(zone) {
  return (zone?.shelves_count || 0) * TRAYS_PER_SHELF;
}

/** Allocate trays from orders across the (DB-managed) zones list. */
export function allocateZones(orders, now, zones) {
  const soakZones = zones.filter((z) => z.kind === "soak").sort((a, b) => a.order - b.order);
  const growthZones = zones.filter((z) => z.kind === "growth").sort((a, b) => a.order - b.order);
  const soakTrays = [];
  const growthTrays = [];

  orders.forEach((o) => {
    const start = new Date(o.start_datetime).getTime();
    const hours = Math.max(0, (now - start) / 3_600_000);
    const trayWeight = o.target_weight_per_tray || 5;
    const trayCount = Math.max(0, Math.ceil(o.order_quantity_kg / trayWeight));
    const day =
      hours <= 24 ? 0
        : hours <= 48 ? 1
          : hours <= 72 ? 2
            : hours <= 96 ? 3
              : hours <= 120 ? 4
                : hours <= 144 ? 5
                  : hours <= 168 ? 6
                    : 7;
    const info = {
      orderId: o.id,
      customer: o.customer_name,
      grainType: o.grain_type || "barley",
      day,
      hours,
    };
    const target = hours < ZONE_MOVE_HOURS ? soakTrays : growthTrays;
    for (let i = 0; i < trayCount; i++) target.push(info);
  });

  const byZone = {};
  zones.forEach((z) => (byZone[z.id] = []));

  let idx = 0;
  for (const z of soakZones) {
    const cap = zoneCapacity(z);
    byZone[z.id] = soakTrays.slice(idx, idx + cap);
    idx += cap;
  }
  const soakOverflow = Math.max(0, soakTrays.length - idx);

  idx = 0;
  for (const z of growthZones) {
    const cap = zoneCapacity(z);
    byZone[z.id] = growthTrays.slice(idx, idx + cap);
    idx += cap;
  }
  const growthOverflow = Math.max(0, growthTrays.length - idx);

  return { byZone, soakOverflow, growthOverflow };
}

/** Render shelves for a zone (grouped into "racks" of 4 shelves for visual cleanliness). */
function ShelfGrid({ zone, trays, theme }) {
  const total = zone.shelves_count;
  const racks = [];
  for (let s = 0; s < total; s += SHELVES_PER_RACK) {
    racks.push({ start: s, count: Math.min(SHELVES_PER_RACK, total - s) });
  }

  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${Math.min(racks.length, 4)}, minmax(0, 1fr))` }}
    >
      {racks.map((r, rIdx) => (
        <div key={rIdx} data-testid={`zone-${zone.id}-rack-${rIdx + 1}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold mb-2">
            Rack {rIdx + 1} · {Math.min(r.count * TRAYS_PER_SHELF, Math.max(0, trays.length - r.start * TRAYS_PER_SHELF))}/{r.count * TRAYS_PER_SHELF}
          </div>
          <div className="space-y-1.5">
            {Array.from({ length: r.count }).map((_, sIdx) => {
              const absoluteShelfIdx = r.start + sIdx;
              const shelfStart = absoluteShelfIdx * TRAYS_PER_SHELF;
              const shelfTrays = trays.slice(shelfStart, shelfStart + TRAYS_PER_SHELF);
              return (
                <div
                  key={sIdx}
                  className="flex items-center gap-1.5"
                  data-testid={`zone-${zone.id}-shelf-${absoluteShelfIdx}`}
                >
                  <div className="text-[9px] uppercase tracking-wider text-stone-400 w-3 text-right font-semibold">
                    S{absoluteShelfIdx + 1}
                  </div>
                  <div className="flex-1 grid grid-cols-3 gap-1.5">
                    {Array.from({ length: TRAYS_PER_SHELF }).map((_, tIdx) => {
                      const tray = shelfTrays[tIdx];
                      if (!tray) {
                        return (
                          <div
                            key={tIdx}
                            className="h-4 rounded-md border border-dashed border-stone-300 bg-white/60"
                            title="Empty"
                          />
                        );
                      }
                      const grain = grainTypeMeta(tray.grainType);
                      return (
                        <div
                          key={tIdx}
                          className={`h-4 rounded-md ${theme.tray} relative overflow-hidden`}
                          title={`${tray.customer} · ${grain.label} · Day ${tray.day}`}
                        >
                          <div className={`absolute inset-x-0 bottom-0 h-1 ${grain.dot}`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ZoneCard({ zone, trays, theme }) {
  const cap = zoneCapacity(zone);
  return (
    <div
      className={`rounded-2xl border ${theme.border} ${theme.bg} p-4 sm:p-5`}
      data-testid={`zone-card-${zone.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${theme.accent}`}>
            {theme.label}
          </div>
          <div className="font-display text-base font-medium text-stone-900">{zone.name}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            {zone.shelves_count} shelves · {cap} tray capacity
          </div>
        </div>
        <div className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${theme.headerChip}`}>
          {trays.length} / {cap}
        </div>
      </div>
      <ShelfGrid zone={zone} trays={trays} theme={theme} />
    </div>
  );
}

export function ZonesTile({ zones, orders, now, onOpenDetail, onManageZones }) {
  const { byZone, soakOverflow, growthOverflow } = useMemo(
    () => allocateZones(orders, now, zones),
    [orders, now, zones],
  );

  const totalCapacity = zones.reduce((s, z) => s + zoneCapacity(z), 0);
  const totalUsed = zones.reduce((s, z) => s + (byZone[z.id]?.length || 0), 0);
  const utilization = totalCapacity ? Math.round((totalUsed / totalCapacity) * 100) : 0;
  const overflow = soakOverflow + growthOverflow;

  const tabbed = zones.length >= INLINE_LIMIT + 1;

  // Assign growth theme alternation order
  const growthIdxMap = new Map();
  zones.filter((z) => z.kind === "growth").sort((a, b) => a.order - b.order).forEach((z, i) => growthIdxMap.set(z.id, i));

  const [activeTab, setActiveTab] = useState(zones[0]?.id);

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="zones-tile">
      <div className="flex items-start justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={onOpenDetail}
          data-testid="zones-tile-open-detail"
          className="text-left flex-1 -m-1 p-1 rounded-lg hover:bg-stone-50 transition-colors"
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
            Zones
          </div>
          <div className="text-xs text-stone-500 mt-1">
            {zones.length} zone{zones.length === 1 ? "" : "s"} ·{" "}
            {zones.filter((z) => z.kind === "soak").length} soak ·{" "}
            {zones.filter((z) => z.kind === "growth").length} growth · move at {ZONE_MOVE_HOURS}h
            {overflow > 0 && (
              <span className="ml-2 text-rose-600 font-semibold">
                · {overflow} tray{overflow === 1 ? "" : "s"} over capacity
              </span>
            )}
          </div>
        </button>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-display text-2xl font-light tabular-nums text-stone-900">
              {totalUsed}<span className="text-stone-400">/{totalCapacity}</span>
            </div>
            <div className="text-[10px] uppercase tracking-wide text-stone-500 mt-0.5">
              {utilization}% utilised
            </div>
          </div>
          <div className="p-2.5 rounded-xl bg-stone-100 text-stone-600">
            <LayoutGrid size={16} strokeWidth={1.75} />
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="manage-zones-btn"
            onClick={onManageZones}
            className="gap-2 border-stone-200 hover:bg-stone-50"
          >
            <Settings2 size={14} strokeWidth={1.75} />
            <span className="hidden sm:inline">Manage</span>
          </Button>
        </div>
      </div>

      {tabbed ? (
        <TabbedZones
          zones={zones}
          byZone={byZone}
          growthIdxMap={growthIdxMap}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      ) : (
        <InlineZones zones={zones} byZone={byZone} growthIdxMap={growthIdxMap} />
      )}
    </div>
  );
}

function InlineZones({ zones, byZone, growthIdxMap }) {
  const sorted = [...zones].sort((a, b) => a.order - b.order);
  const soakZones = sorted.filter((z) => z.kind === "soak");
  const growthZones = sorted.filter((z) => z.kind === "growth");

  return (
    <div className="space-y-3">
      {soakZones.map((z) => (
        <ZoneCard
          key={z.id}
          zone={z}
          trays={byZone[z.id] || []}
          theme={themeForZone(z, 0)}
        />
      ))}
      {growthZones.length > 0 && (
        <div className={`grid gap-3 ${growthZones.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
          {growthZones.map((z) => (
            <ZoneCard
              key={z.id}
              zone={z}
              trays={byZone[z.id] || []}
              theme={themeForZone(z, growthIdxMap.get(z.id) || 0)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TabbedZones({ zones, byZone, growthIdxMap, activeTab, onTabChange }) {
  const sorted = [...zones].sort((a, b) => a.order - b.order);
  const activeZone = sorted.find((z) => z.id === activeTab) || sorted[0];
  if (!activeZone) return null;
  const theme = themeForZone(activeZone, growthIdxMap.get(activeZone.id) || 0);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="zone-tabs">
        {sorted.map((z) => {
          const zTheme = themeForZone(z, growthIdxMap.get(z.id) || 0);
          const used = byZone[z.id]?.length || 0;
          const cap = zoneCapacity(z);
          const isActive = z.id === activeZone.id;
          return (
            <button
              key={z.id}
              type="button"
              onClick={() => onTabChange(z.id)}
              data-active={isActive}
              data-testid={`zone-tab-${z.id}`}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all border
                ${isActive
                  ? `${zTheme.headerChip} border-transparent shadow-sm`
                  : `bg-white text-stone-700 border-stone-200 hover:bg-stone-50`}
              `}
            >
              <div className="flex items-center gap-2">
                <span className="font-display">{z.name}</span>
                <span className={`text-[10px] tabular-nums ${isActive ? "opacity-90" : "text-stone-500"}`}>
                  {used}/{cap}
                </span>
              </div>
              <div className={`text-[9px] uppercase tracking-wider mt-0.5 ${isActive ? "opacity-80" : "text-stone-500"}`}>
                {z.kind === "soak" ? "Soak/Dark" : "Growth"}
              </div>
            </button>
          );
        })}
      </div>
      <ZoneCard zone={activeZone} trays={byZone[activeZone.id] || []} theme={theme} />
    </div>
  );
}

/** Read-only zone breakdown for the detail dialog. */
export function ZonesDetailDialog({ zones, orders, now, open, onOpenChange, onRowClick }) {
  const { byZone } = useMemo(() => allocateZones(orders, now, zones), [orders, now, zones]);

  const groupTrays = (trays) => {
    const map = new Map();
    trays.forEach((t) => {
      const cur = map.get(t.orderId) || { ...t, trays: 0 };
      cur.trays += 1;
      map.set(t.orderId, cur);
    });
    return Array.from(map.values()).sort((a, b) => a.customer.localeCompare(b.customer));
  };

  const sorted = [...zones].sort((a, b) => a.order - b.order);
  const growthIdxMap = new Map();
  sorted.filter((z) => z.kind === "growth").forEach((z, i) => growthIdxMap.set(z.id, i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px] max-h-[92vh] overflow-y-auto" data-testid="zones-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Zones Overview</DialogTitle>
          <DialogDescription>
            {zones.length} zones · orders move from Soak/Dark → Growth after {ZONE_MOVE_HOURS}h (12h soak + 48h dark).
            Each shelf holds {TRAYS_PER_SHELF} trays. Overflow trays beyond a zone's capacity won't appear in the rack visual.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {sorted.map((z) => {
            const theme = themeForZone(z, growthIdxMap.get(z.id) || 0);
            const rows = groupTrays(byZone[z.id] || []);
            return (
              <div key={z.id} className={`rounded-2xl border ${theme.border} ${theme.bg} p-5`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${theme.accent}`}>
                      {theme.label}
                    </div>
                    <div className="font-display text-lg font-medium text-stone-900">{z.name}</div>
                    <div className="text-xs text-stone-500">
                      {z.shelves_count} shelves · {zoneCapacity(z)} trays capacity
                    </div>
                  </div>
                  <div className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${theme.headerChip}`}>
                    {byZone[z.id]?.length || 0} / {zoneCapacity(z)} trays
                  </div>
                </div>
                {rows.length === 0 ? (
                  <div className="text-sm text-stone-500 italic py-3">Empty — no orders in this zone.</div>
                ) : (
                  <div className="rounded-xl bg-white/80 border border-stone-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-white/70 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                        <tr>
                          <th className="text-left px-3 py-2">Customer</th>
                          <th className="text-left px-3 py-2">Grain</th>
                          <th className="text-left px-3 py-2">Day</th>
                          <th className="text-right px-3 py-2">Trays</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => {
                          const g = grainTypeMeta(r.grainType);
                          return (
                            <tr key={r.orderId} className="border-t border-stone-100">
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    onOpenChange(false);
                                    const o = orders.find((x) => x.id === r.orderId);
                                    if (o) onRowClick?.(o);
                                  }}
                                  className="font-medium text-stone-900 hover:text-emerald-700 hover:underline underline-offset-2"
                                >
                                  {r.customer}
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${g.chip}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} />
                                  {g.label}
                                </span>
                              </td>
                              <td className="px-3 py-2 tabular-nums">{r.day} / 7</td>
                              <td className="px-3 py-2 tabular-nums text-right font-medium">{r.trays}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
